import { useEffect, useRef } from "react";
import { toast } from "sonner";

// Açık formların taslağı — adres bazlı: { [adres]: { at, drafts: { anahtar: durum } } }
// Saklanır: toast bağlantısıyla geçişte, sayfa yenilenir/kapanırken VE sayfa içi gezinmede (form bileşeni kapanırken). Aynı adrese dönülünce geri yüklenir (4 saat).
const KEY = "pas.navDraft";
const TTL = 4 * 60 * 60 * 1000;
const registry = new Map();      // anahtar → { get, back }
const listeners = new Set();
let suspended = false;           // çıkış sırasında taslak yazılmasın

export const DRAFT_LABELS = {
  personel: "Personel kartı", "hizmet-veri-girisi": "Hizmet veri girişi", cari: "Cari kartı", fatura: "Fiş / Fatura", kredi: "Banka kredisi", "kredi-karti": "Kredi kartı",
  "satis-pazarlama": "Satış Pazarlama kartı", proje: "Proje kartı", "butce-kalemi": "Bütçe kalemi", demirbas: "Demirbaş kartı", "proje-maliyet": "Proje maliyeti", cek: "Çek", "taseron-cari": "Taşeron cari kartı",
  "ts-auto": "Puantaj satırları", "tt-auto": "Geçici görevlendirme", "fm-draft": "FM saat girişi",
};
// bu sayfadaki taslağa "Git": forma kaydır
export const DRAFT_FOCUS = { personel: '[data-testid="personnel-form"]', "hizmet-veri-girisi": '[data-testid="entry-project"]', "ts-auto": '[data-testid="personnel-timesheet"]', "tt-auto": '[data-testid="temp-timesheet"]', "fm-draft": '[data-testid="fm-hours-sheet"]' };

const here = () => window.location.pathname + window.location.search;
const notify = () => listeners.forEach((f) => { try { f(); } catch { /* dinleyici hatası */ } });
function readStore() { try { const s = JSON.parse(sessionStorage.getItem(KEY) || "{}"); return s && typeof s === "object" && !Array.isArray(s) && !s.drafts ? s : {}; } catch { return {}; } }
function writeStore(s) { if (Object.keys(s).length) sessionStorage.setItem(KEY, JSON.stringify(s)); else sessionStorage.removeItem(KEY); notify(); }
function putDraft(back, key, state) {
  if (suspended) return;
  const s = readStore();
  const e = s[back] || { at: Date.now(), drafts: {} };
  e.drafts[key] = state; e.at = Date.now(); s[back] = e;
  writeStore(s);
}
export function dropDraft(back, key) {
  const s = readStore();
  if (s[back]) { delete s[back].drafts[key]; if (!Object.keys(s[back].drafts).length) delete s[back]; }
  writeStore(s);
}
export function suspendNavDrafts(ms = 3000) { suspended = true; setTimeout(() => { suspended = false; }, ms); }

export function saveDraftsForReturn(back) {
  let dirty = 0;   // soft: yalnız seçim/bağlam ya da açılıp DEĞİŞTİRİLMEMİŞ kayıt — saklanır ama uyarı/rozet tetiklemez
  registry.forEach((r, k) => { try { const v = r.get(); if (v) { putDraft(back, k, v); if (!v.soft) dirty++; } } catch { /* taslak alınamadı */ } });
  return dirty;
}

// kirlilik tabanı: formun açıldığı andaki durum; form tabandan farklı değilse "kaydedilmemiş değişiklik" yoktur
const draftId = (v) => v.editing ?? v.editEntry?.id ?? v.activeKey ?? "__";
const draftSnap = (v) => JSON.stringify(v, (k, x) => (k === "soft" || k === "_base" ? undefined : x));
function withDirty(v, baseRef) {
  const b = baseRef.current;
  if (!v) { if (!b.pinned) baseRef.current = { id: undefined, snap: null }; return null; }   // pinned: geri yükleme tabanı, form render'ı beklerken (StrictMode çift efekt) silinmesin
  const id = draftId(v);
  const snap = draftSnap(v);
  if (b.id !== id) baseRef.current = { id, snap };
  else if (b.pinned) baseRef.current = { id, snap: b.snap };
  return { ...v, soft: v.soft === true || snap === baseRef.current.snap, _base: baseRef.current.snap };
}

if (typeof window !== "undefined") {
  const snapshot = () => saveDraftsForReturn(here());
  window.addEventListener("pagehide", snapshot);
  window.addEventListener("beforeunload", (e) => { if (snapshot()) { e.preventDefault(); e.returnValue = "Kaydetmediğiniz değişiklikler var."; } });
}

function takeDraft(key) {
  const back = here();
  const s = readStore();
  const e = s[back];
  if (!e) return null;
  if (Date.now() - (e.at || 0) > TTL) { delete s[back]; writeStore(s); return null; }
  const d = e.drafts?.[key];
  if (!d) return null;
  delete e.drafts[key];
  if (!Object.keys(e.drafts).length) delete s[back];
  writeStore(s);
  return d;
}

// getState: form açıksa saklanacak nesne, değilse null; applyState: taslağı forma geri yükler
export function useNavDraft(key, getState, applyState) {
  const getRef = useRef(getState); getRef.current = getState;
  const applyRef = useRef(applyState); applyRef.current = applyState;
  const baseRef = useRef({ id: undefined, snap: null });
  useEffect(() => { withDirty(getRef.current(), baseRef); });   // her render: form açıldığı render'da taban alınır (sonraki yazımlar kirli sayılır)
  useEffect(() => {
    const r = { get: () => withDirty(getRef.current(), baseRef), back: here() };
    registry.set(key, r);
    return () => { try { const v = r.get(); if (v) putDraft(r.back, key, v); } catch { /* taslak alınamadı */ } registry.delete(key); notify(); };   // sayfa içi gezinme: açık form taslağı saklanır
  }, [key]);
  useEffect(() => { const r = registry.get(key); if (r) r.back = here(); });   // sayfanın güncel adresi (filtre parametreleri değişebilir)
  useEffect(() => {
    const d = takeDraft(key);
    if (!d) return;
    baseRef.current = { id: draftId(d), snap: d._base ?? null, pinned: true };   // geri yüklenen taslak, kaydedildiği andaki tabanına göre kirli kalır
    applyRef.current(d);
    if (!d.soft) toast.info("Kaydedilmemiş girişiniz geri yüklendi", { description: "Kaldığınız yerden devam edebilirsiniz.", id: `nav-draft-${key}` });
  }, [key]);
}
